import { useState } from 'react';
import { Download, FileText, FileSpreadsheet, Image as ImageIcon, File } from 'lucide-react';
import { FilePreviewModal } from '../resources/FilePreviewModal';
import type { FileKind } from '../../lib/projectsApi';
import { saveFile } from '../../lib/saveFile';

/**
 * Inline file card in an assistant message.
 *
 * The chat Markdown renderer hands a fenced ```firmio-file block here: the
 * JSON spec a resource tool returned (`chatBlock`) describing one stored file
 * — a project's contract, a client's NDA, the firm's registration certificate
 * — with its signed download URL. Rendered as a card with the file's kind,
 * size and owner and a download button, so "dă-mi contractul de la RetuRO"
 * ends with the file in the chat, not with a path to go look for it.
 *
 * The URL is a storage-proxy link (`/api/storage/download/<token>`); the
 * backend re-signs it whenever a conversation is reloaded.
 */

export interface FileCardSpec {
  name: string;
  url: string | null;
  fileType?: string | null;
  fileSize?: number | null;
  category?: string | null;
  owner?: string | null;
  description?: string | null;
}

export function parseFileSpec(text: string): FileCardSpec | null {
  try {
    const raw = JSON.parse(text) as Record<string, unknown>;
    if (!raw || typeof raw !== 'object' || typeof raw.name !== 'string') return null;
    const url = typeof raw.url === 'string' && raw.url.trim() ? raw.url : null;
    return {
      name: raw.name,
      url,
      fileType: typeof raw.fileType === 'string' ? raw.fileType : null,
      fileSize: typeof raw.fileSize === 'number' ? raw.fileSize : null,
      category: typeof raw.category === 'string' ? raw.category : null,
      owner: typeof raw.owner === 'string' ? raw.owner : null,
      description: typeof raw.description === 'string' ? raw.description : null,
    };
  } catch {
    return null;
  }
}

export type FileCardKind = 'pdf' | 'image' | 'sheet' | 'doc' | 'file';

export function fileCardKind(name: string, fileType?: string | null): FileCardKind {
  const n = name.toLowerCase();
  const t = (fileType ?? '').toLowerCase();
  if (n.endsWith('.pdf') || t === 'pdf' || t.includes('pdf')) return 'pdf';
  if (/\.(png|jpe?g|gif|webp|svg|bmp|tiff?)$/.test(n) || t.startsWith('image/')) return 'image';
  if (/\.(xlsx?|xlsm|csv|ods)$/.test(n) || /xls|csv|sheet/.test(t)) return 'sheet';
  if (/\.(docx?|odt|txt|md|rtf|pptx?)$/.test(n) || /doc|word|txt|md/.test(t)) return 'doc';
  return 'file';
}

/** Storage keys keep names URL-encoded; people expect to read "Copy of …". */
export function displayFileName(name: string): string {
  if (!/%[0-9A-Fa-f]{2}/.test(name)) return name;
  try {
    return decodeURIComponent(name);
  } catch {
    return name;
  }
}

/** The preview modal's kind vocabulary has no spreadsheet bucket. */
function toFileKind(kind: FileCardKind): FileKind {
  return kind === 'sheet' ? 'doc' : kind;
}

export function formatFileSize(bytes: number | null | undefined): string | null {
  if (!bytes || bytes <= 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace(/\.0$/, '')} MB`;
}

const CATEGORY_LABELS: Record<string, string> = {
  contracte: 'Contract',
  design: 'Design',
  alte: 'Resursă',
  CONTRACT: 'Contract',
  INVOICE: 'Factură',
  PROPOSAL: 'Propunere',
  NDA: 'NDA',
  OTHER: 'Document',
  REGISTRATION_CERTIFICATE: 'Certificat de înregistrare',
  CONSTATATOR: 'Certificat constatator',
  STATUT: 'Statut / Act constitutiv',
};

function KindIcon({ kind, className }: { kind: FileCardKind; className?: string }) {
  if (kind === 'image') return <ImageIcon className={className} />;
  if (kind === 'sheet') return <FileSpreadsheet className={className} />;
  if (kind === 'pdf' || kind === 'doc') return <FileText className={className} />;
  return <File className={className} />;
}

export function ChatFileCard({ spec }: { spec: FileCardSpec }) {
  const kind = fileCardKind(spec.name, spec.fileType);
  const name = displayFileName(spec.name);
  const description = spec.description ? displayFileName(spec.description) : null;
  const size = formatFileSize(spec.fileSize);
  const category = spec.category ? (CATEGORY_LABELS[spec.category] ?? spec.category) : null;
  const meta = [category, spec.fileType?.toUpperCase(), size].filter(Boolean).join(' · ');
  // Tapping the card opens the file in the same preview modal the Resources
  // tabs use (PDF inline, image lightbox); the button is the plain download.
  const [previewOpen, setPreviewOpen] = useState(false);

  return (
    <div className="my-1 flex items-center gap-3 rounded-lg border border-border bg-muted/40 px-3 py-2.5">
      <button
        type="button"
        onClick={() => spec.url && setPreviewOpen(true)}
        disabled={!spec.url}
        className="flex min-w-0 flex-1 items-center gap-3 text-left disabled:cursor-default"
        title={spec.url ? 'Previzualizează' : undefined}
      >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
          <KindIcon kind={kind} className="h-5 w-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-foreground" title={name}>
            {name}
          </span>
          <span className="block truncate text-xs text-muted-foreground">
            {[meta, spec.owner].filter(Boolean).join(' — ') || 'Fișier'}
          </span>
          {description ? (
            <span
              className="mt-0.5 block truncate text-xs text-muted-foreground"
              title={description}
            >
              {description}
            </span>
          ) : null}
        </span>
      </button>
      {previewOpen && spec.url ? (
        <FilePreviewModal
          file={{ name, kind: toFileKind(kind), downloadUrl: spec.url }}
          onClose={() => setPreviewOpen(false)}
        />
      ) : null}
      {spec.url ? (
        <button
          type="button"
          onClick={() => void saveFile(spec.url as string, name)}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs font-medium text-foreground hover:bg-accent"
        >
          <Download size={14} />
          Descarcă
        </button>
      ) : (
        <span className="shrink-0 text-xs text-muted-foreground">Link indisponibil</span>
      )}
    </div>
  );
}
